import {React, useRef, useState, useEffect}  from 'react';
import SMCButton from '../../Simple/SMCButton';
import {useTranslation} from 'react-i18next';
import tooltipIcon from '../../../assets/images/login_help.svg';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import { makeStyles } from '@mui/styles';
import ButtonsContainerStyles from "../../../pages/EditorPages/ButtonsContainerStyles";

/**
 * 
 * @param {Object} params
 * @param {function} [params.openNewObjectWindow] - Function that recieves a row object, and index, and opens a new object for editing
 * @param {function} [params.openRecommandedFirmwareWindow] - Function that opens recommended firmware modal
 * @param {function} [params.reloadButtonOnClick] - Reload handler the table
 * @param {function} [params.deleteButtonOnClick] - Delete handler for the table
 * @param {function} [params.toggleFilterPane] - Opens / closes filter pane
 * @param {boolean}  [params.canCreate] - True if user has new object creation permissions
 * @param {boolean}  [params.canDelete] - True if user has object deletion permissions
 * @param {boolean}  [params.isFetching] - True if busy fetching 
 * @param {[Object]} [params.actions] - Object describing Actions menu
 * @param {[Object]} [params.tableContent] - Object describing table content for results
 * @param {boolean}  [params.isFilterPaneOpen] - True if filter pane is open
 * @param {boolean}  [params.showSearchBar] - True if should show search bar
 * @param {Array}  [params.selectedObjects] - Table selected objects
 * @param {[Object]} [params.initialSearch] - Initial search
 * @param {function} [params.onSearchChange] - Function to be called on search change
 * @param {[Object]} [params.searchAttribute] - Attribute to search by 
 * @param {boolean}  [params.showFilter] - Show filter 
 * 
 */
    function SMCTableActions({
        openNewObjectWindow,
        openRecommandedFirmwareWindow,
        reloadButtonOnClick,
        deleteButtonOnClick,
        toggleFilterPane,
        canCreate,
        canDelete,
        isFetching,
        actions,
        tableContent,
        isFilterPaneOpen,
        selectedObjects,
        initialSearch,
        onSearchChange,
        searchAttribute,
        showFilter=true,
        isDataFiltered
    }) {

    const { t } = useTranslation();
    const ActionsMenu = actions;
    const freeTextSearchRef = useRef();
    const didMountRef = useRef(true);
    const classes = useStyles();
    const buttonsClasses = ButtonsContainerStyles();


    function getSelectedObjects() {
            let selectedList = '';
            if (selectedObjects && (selectedObjects.length !== 0)) {
                selectedObjects.forEach(
                        (selectedObject, i) => (selectedList += selectedObject + "\n")
                );
                return selectedList;
            }
        }

        const [freeTextSearch, setFreeTextSearch] = useState(
            initialSearch?.freeTextSearch ? initialSearch.freeTextSearch.value : ''
        );
            
        const VerticalMenuLine = () => {
            return (
                <div style={{ height: '50%', width: '1px', backgroundColor: '#EAECEF', float: 'left', marginLeft: '4px', marginRight: '4px' }}></div>        
            );
          };

        useEffect(() => {
            // Don't run on first render
            if (didMountRef.current === true) {
                didMountRef.current = false;
                return;
            }

            const filterRequestObj = {};
            // Free text search and length > 0
            
            if (freeTextSearch) {

                if (searchAttribute.toLowerCase().includes("freeTextSearch".toLowerCase()))
                    filterRequestObj[searchAttribute] = { value: freeTextSearch };
                else
                    filterRequestObj[searchAttribute] = { value: '*'+freeTextSearch+'*' };
            }
                        
            onSearchChange(filterRequestObj);
        }, [
            freeTextSearch, onSearchChange, searchAttribute
        ]);

        function _isDataFiltered(filterObject) {
            if (isDataFiltered) {
                return isDataFiltered(filterObject);
            }
            else {
                if (filterObject)
                    return Object.keys(filterObject).length !== 0;
                else
                    return false;
            }
        }

        return (
            <div className="DrawResultsToolbar" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '30px',
                width: '100%'
            }}>
                <SMCButton labelClassName={buttonsClasses.labelColor} icon="new" variant="ghost" disabled={!canCreate || isFetching} onClick={openNewObjectWindow}>{t('SMC_GEN_NEW')}</SMCButton>
                <VerticalMenuLine/>
                <SMCButton labelClassName={buttonsClasses.labelColor} icon="plus" variant="ghost" disabled={!canCreate || isFetching} onClick={openRecommandedFirmwareWindow}>{t('SMP.FIRMWARE.RECOMMENDED')}</SMCButton>
                <VerticalMenuLine/>
                <SMCButton labelClassName={buttonsClasses.labelColor} icon="refresh" variant="ghost" disabled={isFetching} onClick={reloadButtonOnClick}>{t('SMC_GEN_REFRESH')}</SMCButton>
                <VerticalMenuLine/>
                <SMCButton labelClassName={buttonsClasses.labelColor} icon="delete" variant="ghost" disabled={!canDelete || isFetching} onClick={deleteButtonOnClick}>{t('SMC_GEN_DELETE')}</SMCButton>
                <VerticalMenuLine/>
                {ActionsMenu && <ActionsMenu/>}
                <div style={{width: "10px"}}></div>
                {
                    <SMCTextInput
                        width="auto"
                        height={30}
                        placeholder="Search..."
                        value={freeTextSearch}
                        innerRef={freeTextSearchRef}
                        onChange={e => setFreeTextSearch(e.target.value)}
                        suffixIcon="search"
                        allowClearAll={true} 
                    />
                }
                {showFilter && 
                     <SMCButton 
                             className= {isFilterPaneOpen ? classes.filterOpen : classes.filterClosed}
                            icon={_isDataFiltered(initialSearch) ? "filterWithBadge" : "filter"}
                            variant="ghost" 
                            disabled={isFetching} 
                            onClick={() => toggleFilterPane()} 
                            height="100%"
                            width="100%"                 
                         ></SMCButton>
                } 
                {!isFetching && 
                    <div style={{marginLeft: '3px', position: 'absolute', right: '30px'}}>
                        <span style={{ marginLeft: '3px'}}>{selectedObjects ? selectedObjects.length : 0} of {tableContent ? tableContent.length : 0} {t('SMC_GEN_RESULTS')}</span>
                        <img src={tooltipIcon} title={getSelectedObjects()} alt={''} style={{marginLeft: '3px', height: '10%', width: '10%', 'bottom' : '0'}}></img>
                    </div>
                }
            </div>
        )
}


const useStyles = makeStyles({
    filterOpen: {
        backgroundColor:'#CCCDCF', 
        paddingLeft:'5px',
        border: '1px black'
    },
    filterClosed: {
        paddingLeft:'5px'
    }
})

export default SMCTableActions;